import { useState, useEffect } from "react";
import { Menu, X, Phone } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const NAV_LINKS = [
  { label: "Home", href: "#home" },
  { label: "Services", href: "#services" },
  { label: "About", href: "#founder" },
  { label: "Testimonials", href: "#testimonials" },
  { label: "Contact", href: "#contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (href) => {
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-[#0A0D14]/90 border-b border-white/5 shadow-lg"
          : "bg-transparent"
      }`}
    >
      <div className="container mx-auto px-6 md:px-12 max-w-7xl">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <button
            onClick={() => handleNav("#home")}
            data-testid="logo-link"
            className="flex items-center gap-3 group"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_tax-compliance-hub-30/artifacts/0yf9q3g3_IMG-20260320-WA0000.jpg"
              alt="AVS Associates & Co"
              className="h-12 w-auto"
            />
          </button>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {NAV_LINKS.map((link) => (
              <button
                key={link.href}
                onClick={() => handleNav(link.href)}
                data-testid={`nav-${link.label.toLowerCase()}`}
                className="text-[#94A3B8] hover:text-white text-sm font-work font-medium tracking-wide transition-colors duration-300 relative group"
              >
                {link.label}
                <span className="absolute -bottom-1 left-0 w-0 h-[1px] bg-[#D4AF37] group-hover:w-full transition-all duration-300" />
              </button>
            ))}
          </nav>

          {/* CTA + Mobile Toggle */}
          <div className="flex items-center gap-4">
            <a
              href="tel:+919990072390"
              data-testid="header-call-btn"
              className="hidden md:inline-flex items-center gap-2 bg-[#D4AF37] text-[#0A0D14] font-work font-medium text-sm px-6 py-2.5 hover:bg-[#F4D068] transition-colors"
            >
              <Phone size={14} />
              Consult Now
            </a>
            <button
              data-testid="mobile-menu-toggle"
              className="lg:hidden text-white p-2"
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              {mobileOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-[#0A0D14]/98 backdrop-blur-xl border-t border-white/5"
            data-testid="mobile-menu"
          >
            <div className="container mx-auto px-6 py-6 flex flex-col gap-4">
              {NAV_LINKS.map((link) => (
                <button
                  key={link.href}
                  onClick={() => handleNav(link.href)}
                  data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                  className="text-[#94A3B8] hover:text-white text-base font-work text-left py-2 border-b border-white/5 transition-colors"
                >
                  {link.label}
                </button>
              ))}
              <a
                href="tel:+919990072390"
                data-testid="mobile-call-btn"
                className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0A0D14] font-work font-medium text-sm px-6 py-3 mt-2"
              >
                <Phone size={14} />
                Consult Now
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
